import { Box } from "@chakra-ui/react";
import { colors, customEvent, Spacing } from "goi_common";
import { baseColors, BaseStack } from "goi_common/src/design-system";
import { BaseButton } from "goi_common/src/design-system/components/BaseButton";
import { ReactNode, useState } from "react";

import { patchJangjiRecommendRequestConsultTime } from "@/apis/jangjiRecommend";
import { ConsultantIllustration } from "@/components/common/ConsultantIllustration";
import { useDialog } from "@/hooks/useDialog";
import { useGetServerTime } from "@/hooks/useGetServerTime";

/**
 * 장지 여유 고객이 상담 받을 시간을 고르는 블록.
 *
 * 견적의 `PreferredConsultTimeSelect`(MatcRecommendationContainer)와 같은 기능이지만
 * **복제했다.** 그쪽은 견적 요청서 id 로 견적 API 를 직접 부르고 화면 전체를 차지한다.
 * 이쪽은 장지 요청서 id 를 쓰고 결과 화면 안의 한 블록이다. 두 번째 사용처라 아직
 * 공통화하지 않는다(3번 규칙). 세 번째가 생기면 그때 뺀다.
 *
 * 기획은 wiki/plans/jangji-daybreak-consult-time.md 3-2.
 */

type ConsultTimeSlot = {
  label: string;
  start: string;
  end: string;
};

/**
 * 서버 `ALLOWED_SLOTS` 와 같아야 한다. 그 밖의 값은 400 이다.
 * 점심 12-13 은 없다. 라벨은 견적과 같은 포맷을 쓴다.
 */
const MORNING_SLOTS: ConsultTimeSlot[] = [
  { label: "09:00-10:00", start: "09:00", end: "10:00" },
  { label: "10:00-11:00", start: "10:00", end: "11:00" },
  { label: "11:00-12:00", start: "11:00", end: "12:00" },
];

const AFTERNOON_SLOTS: ConsultTimeSlot[] = [
  { label: "01:00-02:00", start: "13:00", end: "14:00" },
  { label: "02:00-03:00", start: "14:00", end: "15:00" },
  { label: "03:00-04:00", start: "15:00", end: "16:00" },
  { label: "04:00-05:00", start: "16:00", end: "17:00" },
];

/**
 * 이벤트 path 는 같은 화면의 다른 이벤트와 같이 `jangji_done` 을 쓴다.
 *
 * `facility_match_recommendation` 은 29자라 `click-{path}-{trigger}` 가 GA4 40자에서
 * 잘린 뒤 trigger 가 4자만 남는다. 그러면 `db_slot-select` 와 `db_submit-cta` 가 둘 다
 * `db_s` 로 잘려 구분이 안 된다. `jangji_done` 은 11자라 22자가 살아남는다.
 *
 * trigger 이름은 견적과 같게 둔다. 같은 기능이라 채널 간 비교가 된다.
 */
const EVENT_PATH = "jangji_done" as const;

interface JangjiConsultTimeSelectProps {
  /** 장지 추천 요청서 id. 조회 응답의 `id` */
  requestId: number;
  /**
   * 여유 설문에서 고른 답변 원문.
   *
   * 이 블록은 상담행 답변일 때만 뜨므로 실제로 들어오는 값은 두 가지뿐이다.
   * `전반적인 장례 비용, 절차 등도 함께 알아보고 있어요.` / `장례식장, 화장장 등 시설 정보도 궁금해요.`
   */
  surveyAnswer?: string;
}

export function JangjiConsultTimeSelect({ requestId, surveyAnswer }: JangjiConsultTimeSelectProps) {
  const [selected, setSelected] = useState<ConsultTimeSlot | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  /**
   * 제출 성공 후 완료 문구로 바꾼다.
   *
   * 저장한 시간은 조회 응답에 안 실려서 새로고침하면 다시 선택 화면이 된다. 서버가
   * 재선택을 허용하므로(같은 행을 덮어쓴다) 데이터가 어긋나지는 않는다.
   */
  const [submittedLabel, setSubmittedLabel] = useState<string | null>(null);
  const [submittedKind, setSubmittedKind] = useState<"holiday" | "weekend" | null>(null);
  const { onOpen, onClose } = useDialog();

  /**
   * 주말·연휴 접수는 서버가 슬롯을 다음 영업일로 붙인다.
   * 완료 문구는 제출 시점 유형으로 고정한다. 연휴면 '연휴가 끝나는 날', 주말이면 '월요일'.
   */
  const { data: serverTime } = useGetServerTime();
  const isHolidayCopy = serverTime?.consult_defer_kind === "holiday";
  const isWeekendCopy = serverTime?.consult_defer_kind === "weekend";

  const handleSelect = (slot: ConsultTimeSlot) => {
    setSelected(slot);
    customEvent({
      newGtm: {
        path: EVENT_PATH,
        trigger_and_target: "db_slot-select",
        text: `${slot.start}-${slot.end}`,
      },
    });
  };

  const handleSubmit = async () => {
    if (!selected || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await patchJangjiRecommendRequestConsultTime(requestId, {
        start: selected.start,
        end: selected.end,
      });

      customEvent({
        newGtm: {
          path: EVENT_PATH,
          trigger_and_target: "db_submit-cta",
          text: `${selected.start}-${selected.end}`,
        },
      });

      setSubmittedKind(isHolidayCopy ? "holiday" : isWeekendCopy ? "weekend" : null);
      setSubmittedLabel(`${selected.start} ~ ${selected.end}`);
      onOpen({
        type: 1,
        header: "상담 신청이 완료되었어요",
        children: (
          <>
            고객님이 선택하신 시간에
            <br />
            정리한 내용을 전달드리겠습니다.
          </>
        ),
        footerProps: {
          text: "확인",
          // BaseButton primary 와 같은 테마(orange650)
          backgroundColor: colors.orange650,
          color: colors.white,
          borderRadius: "6px",
          onClick: () => {
            customEvent({
              newGtm: {
                path: EVENT_PATH,
                trigger_and_target: "db_confirm-cta",
              },
            });
            onClose();
          },
        },
        closeOnOverlayClick: false,
      });
    } catch {
      onOpen({
        type: 1,
        header: "오류",
        children: <>요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.</>,
        footerProps: {
          text: "확인",
          onClick: () => {
            onClose();
          },
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submittedLabel) {
    return (
      <SectionFrame>
        <ConsultantIllustration />

        <Spacing gap={24} />

        <Box className="headline_2_sb" color={baseColors.label_normal} textAlign="center">
          {submittedKind === "holiday"
            ? "연휴가 끝나는 날 "
            : submittedKind === "weekend"
            ? "월요일 "
            : ""}
          {submittedLabel} 에<br />
          전달드릴게요
        </Box>

        <Spacing gap={12} />

        <Box className="body_1_normal_m" color={baseColors.label_alternative_1} textAlign="center">
          정리가 완료되는대로
          <br />
          선택하신 시간에 연락드릴게요.
        </Box>
      </SectionFrame>
    );
  }

  return (
    <SectionFrame>
      <ConsultantIllustration />

      <Spacing gap={24} />

      <Box className="headline_2_sb" color={baseColors.label_normal} textAlign="center">
        장례 전문 상담원이
        <br />
        추가로 궁금해하신 부분에 대해
        <br />
        정리하고 있어요.
      </Box>

      {surveyAnswer && (
        <>
          <Spacing gap={20} />

          {/* 무엇을 정리하고 있는지 고객이 고른 문장 그대로 되짚는다 */}
          <Box
            width="100%"
            padding="14px 16px"
            borderRadius="8px"
            backgroundColor={baseColors.fill_normal}
            className="label_1_m"
            color={baseColors.label_alternative_1}
            textAlign="center"
            wordBreak="keep-all"
          >
            {surveyAnswer}
          </Box>
        </>
      )}

      <Spacing gap={20} />

      <Box className="body_1_normal_m" color={baseColors.label_alternative_1} textAlign="center">
        {isHolidayCopy ? (
          <>
            연휴 접수 장지 추천은 연휴 이후 순차적으로 안내드릴 예정입니다.
            <br />
            장지 추천 정보가 준비되면 선택하신 시간대에 안내해드릴게요.
          </>
        ) : isWeekendCopy ? (
          <>
            주말 접수 건은 순차적으로 검토 후 안내해드리고 있습니다.
            <br />
            정리가 완료되면 월요일 선택하신 시간대에 전달드릴게요.
          </>
        ) : (
          <>
            원하시는 시간대를 선택해 주시면
            <br />
            정리가 완료되는대로 전달드릴게요.
          </>
        )}
      </Box>

      <Spacing gap={32} />

      <BaseStack width="100%" spacing={24}>
        <TimeSlotGroup
          title="오전"
          slots={MORNING_SLOTS}
          selected={selected}
          onSelect={handleSelect}
        />
        <TimeSlotGroup
          title="오후"
          slots={AFTERNOON_SLOTS}
          selected={selected}
          onSelect={handleSelect}
        />
      </BaseStack>

      <Spacing gap={32} />

      <Box width="100%">
        <BaseButton
          type="primary"
          size="large"
          disabled={!selected || isSubmitting}
          onClick={handleSubmit}
        >
          <Box className="body_16_m">선택한 시간에 전달받기</Box>
        </BaseButton>
      </Box>
    </SectionFrame>
  );
}

/**
 * 섹션 껍데기. 위에 구분선을 둬서 장지 결과 내용과 시각적으로 끊는다.
 *
 * 이 블록은 결과 화면 맨 아래에 붙는다. 위쪽은 고객이 낸 장지 견적 내용이고 여기부터는
 * 설문에서 추가로 물어본 것에 대한 응답이라, 같은 흐름으로 읽히면 안 된다.
 */
function SectionFrame({ children }: { children: ReactNode }) {
  return (
    <BaseStack
      width="100%"
      alignItems="center"
      padding="40px 20px"
      borderTop={`8px solid ${baseColors.line_alternative}`}
    >
      {children}
    </BaseStack>
  );
}

function TimeSlotGroup({
  title,
  slots,
  selected,
  onSelect,
}: {
  title: string;
  slots: ConsultTimeSlot[];
  selected: ConsultTimeSlot | null;
  onSelect: (slot: ConsultTimeSlot) => void;
}) {
  return (
    <BaseStack width="100%" spacing={12}>
      <Box className="label_1_sb" color={baseColors.label_normal}>
        {title}
      </Box>
      <BaseStack direction="horizontal" spacing={8} flexWrap="wrap">
        {slots.map((slot) => {
          const isSelected = selected?.start === slot.start && selected?.end === slot.end;
          return (
            <Box
              key={slot.start}
              as="button"
              type="button"
              // 라벨 포맷 동일(09:00-10:00) - 가로 늘리지 않고 칩 크기만 통일
              width="118px"
              flexShrink={0}
              cursor="pointer"
              py="10px"
              borderRadius="8px"
              border={`1px solid ${isSelected ? colors.orange650 : baseColors.line_accent}`}
              backgroundColor={isSelected ? colors.orange650 : baseColors.white}
              color={isSelected ? colors.white : baseColors.label_normal}
              className="label_1_m"
              textAlign="center"
              whiteSpace="nowrap"
              onClick={() => onSelect(slot)}
            >
              {slot.label}
            </Box>
          );
        })}
      </BaseStack>
    </BaseStack>
  );
}
